import React from 'react';
import { pathOr } from 'ramda';
import Flow from '../flow';
import GettingStarted from './GettingStarted';
import { connect } from 'react-redux';
import { deleteFlow, fetchAllFlows } from '../../actions/reduxActions/flows';
import EditorActions from '../../actions/editor';
import Loader from '../Loader';
import FlowTile from './FlowTile';
import Pager from '../Pager';
import SearchInput from '../SearchInput';
import { takePage, stringContains, stringReplace, generateRouteUrl } from '../../utils/core';
import translations from '../../translations';
import Confirm from '../generic/confirm';
import { tabDetailsObjects } from '../../config/admin';
import { navigate } from '@reach/router';
import { FormControl } from 'react-bootstrap';
import { FLOW_SORT_COLUMNS } from '../../constants';

export class FlowList extends React.Component {

    PAGE_SIZE = 20;

    adminPageRef = React.createRef();

    state = {
        pagedFlows: {},
        search: '',
        searchValue: '',
        selectedFlow: {
            developerName: '',
        },
        showDeleteModal: false,
        showNewFlowSystemFlow: false,
        page: 1,
        sort: FLOW_SORT_COLUMNS.LAST_MODIFIELD_DESC,
    };

    componentDidMount() {
        this.fetchFlows();
    }

    componentDidUpdate(prevProps) {
        // If the user has switched tenants but stayed here, we need to reload the flow list and close the system flow
        // Or if the user changes to this tab from another tab (e.g. Import/Export), reload the flow list too
        if (
            prevProps.tenantId !== this.props.tenantId ||
            (!prevProps.isEditorActive && this.props.isEditorActive)
        ) {

            // Tenant ID gets set to null when signing out
            if (this.props.tenantId !== null) {
                this.fetchFlows();
                this.setState({
                    showNewFlowSystemFlow: false,
                    search: '',
                    searchValue: '',
                });
            }
        }
    }

    doesFlowMatchSearch = flow => [
        flow.developerName,
        flow.developerSummary,
        pathOr('', ['id', 'id'], flow),
    ].some(str => stringContains(str, this.state.search, false));

    fetchFlows = () => {
        this.props.fetchAllFlows(
            this.props.tenantId,
            this.props.authenticationToken,
        );
    };

    onChangeSearch = (query) => {
        this.setState({
            page: 1,
            search: query,
        });
    };

    onChange = (e) => {
        this.setState({ searchValue: e.target.value });
    }

    onClickDeleteFlow = (flow) => {
        this.setState({
            selectedFlow: flow,
            showDeleteModal: true,
        });
    };

    onClickDeleteFlowConfirmation = async () => {
        await this.props.deleteFlow(this.state.selectedFlow.id.id);
        this.setState({
            showDeleteModal: false,
        });
        this.fetchFlows();
    };

    /**
     * @param flow a flow object
     */
    onClickEditFlow = (flow) => {
        const route = generateRouteUrl({
            tabType: 'flow',
            tenantId: this.props.tenantId,
            options: {
                flowId: flow.id.id,
            },
        });
        navigate(route);
    };

    onClickFlowLibrary = () => {
        EditorActions.openEditor(tabDetailsObjects.flowLibrary);
    };

    onClickNewFlow = () => {
        this.setState({
            showNewFlowSystemFlow: true,
        });
    };

    onClickRefresh = () => {
        this.fetchFlows();
    };

    onSortChange = e => this.setState({
        page: 1,
        sort: parseInt(e.target.value),
    });

    /**
     * When the "Flows" system flow completes (i.e. gives a DONE invoke type), this method is called to determine
     * what the tooling should do.
     *
     * @param callback the callback coming from the system flow
     * @param response the response given back from the system flow, including any output values
     */
    onFlowDone = (callback, response) => {
        const mapElementName = response.mapElementInvokeResponses[0].developerName;

        switch (mapElementName) {
        case 'Flow List':
            // This outcome should just fall back to showing the flow list
            break;
        case 'New Flow': {
            // This outcome means a flow was created, so we need to open it up for editing
            const createdFlow = response.outputs.find(o => o.developerName === 'Selected Flow').objectData[0];

            const flow = {
                developerName: createdFlow.properties.find(p => p.developerName === 'DeveloperName').contentValue,
                id: {
                    id: createdFlow.externalId,
                },
            };

            this.onClickEditFlow(flow);
            this.fetchFlows();

            break;
        }

        default:
            break;
        }

        // Finally, we always want to end the system flow
        this.setState({
            showNewFlowSystemFlow: false,
        });
    };

    onToggleDeleteModal = () => {
        this.setState({
            showDeleteModal: !this.state.showDeleteModal,
        });
    };

    render() {
        // If we need to run the system flow (i.e. someone clicked the "New Flow" button), we show only that
        if (this.state.showNewFlowSystemFlow) {
            return (
                <Flow
                    tenantId={this.props.adminTenantId}
                    flowName="Flows"
                    component="main"
                    inputs={this.props.inputs}
                    options={this.props.options}
                    callbacks={[{ execute: this.onFlowDone, type: 'done' }]}
                    restart={this.props.restart}
                    key={`flows-${this.props.tenantId}`}
                />
            );
        }

        const sortOptions = [
            <option value={FLOW_SORT_COLUMNS.LAST_MODIFIELD_DESC} key={FLOW_SORT_COLUMNS.LAST_MODIFIELD_DESC}>Last Modified (Descending)</option>,
            <option value={FLOW_SORT_COLUMNS.LAST_MODIFIELD_ASC} key={FLOW_SORT_COLUMNS.LAST_MODIFIELD_ASC}>Last Modified (Ascending)</option>,
            <option value={FLOW_SORT_COLUMNS.NAME_DESC} key={FLOW_SORT_COLUMNS.NAME_DESC}>Name (Descending)</option>,
            <option value={FLOW_SORT_COLUMNS.NAME_ASC} key={FLOW_SORT_COLUMNS.NAME_ASC}>Name (Ascending)</option>,                        
        ];
        
        const filteredFlows = this.props.flows.filter(this.doesFlowMatchSearch).sort((a, b) => {
            switch (this.state.sort) {
            case FLOW_SORT_COLUMNS.NAME_ASC:
            default:
                return a.developerName.localeCompare(b.developerName);

            case FLOW_SORT_COLUMNS.NAME_DESC:
                return b.developerName.localeCompare(a.developerName);

            case FLOW_SORT_COLUMNS.LAST_MODIFIELD_DESC:
                return b.dateModified.localeCompare(a.dateModified);

            case FLOW_SORT_COLUMNS.LAST_MODIFIELD_ASC:
                return a.dateModified.localeCompare(b.dateModified);
            }
        });
        this.state.pagedFlows = takePage(this.PAGE_SIZE, this.state.page, filteredFlows);

        let content;
        if (this.props.isLoading) {
            content = <Loader message="Loading flows" />;
        } else if (this.props.flows.length === 0) {
            // If we don't have any flows in the tenant, show a dialog about Flow Library, otherwise list the flows
            content = <GettingStarted onNewFlowClick={this.onClickNewFlow} onFlowLibraryClick={this.onClickFlowLibrary} />;
        } else {
            content = (
                <>
                    <div className="margin-top-large flow-tiles-container">
                        {
                            this.state.pagedFlows
                                .map(flow => (
                                    <FlowTile
                                        flow={flow}
                                        key={flow.id.id}
                                        onClickDeleteFlow={this.onClickDeleteFlow}
                                        onClickEditFlow={this.onClickEditFlow}
                                        tenantId={this.props.tenantId}
                                    />
                                ))
                        }
                    </div>
                    <Pager
                        page={this.state.page}
                        pageSize={this.PAGE_SIZE}
                        total={filteredFlows.length}
                        onPageClick={
                            (pageNumber) => {
                                this.setState({ page: pageNumber });
                                this.adminPageRef.current.scrollTop = 0;
                            }
                        }
                    />
                </>
            );
        }

        return (
            <div className="admin-page" ref={this.adminPageRef}>

                <Confirm
                    show={this.state.showDeleteModal}
                    title={translations.FLOW_delete_confirmation_title}
                    messages={[
                        stringReplace(translations.FLOW_delete_confirmation_message, {
                            flowName: this.state.selectedFlow.developerName,
                        }),
                        translations.GENERAL_cannot_be_undone,
                    ]}
                    buttonStyle="danger"
                    buttonCaption="Delete"
                    onCancel={this.onToggleDeleteModal}
                    onConfirm={this.onClickDeleteFlowConfirmation}
                    isInProgress={this.props.isFlowDeleting}
                />

                <h1>Flows</h1>

                <div className="flex">
                    <SearchInput onSearch={this.onChangeSearch} onChange={this.onChange} value={this.state.searchValue} />

                    <div className="flow_filters">
                        <FormControl
                            componentClass="select"
                            value={this.state.sort}
                            onChange={this.onSortChange}
                        >
                            {sortOptions}
                        </FormControl>
                    </div>

                    <div className="flex-child-right">
                        <button className="btn btn-success btn-sm" onClick={this.onClickNewFlow} title="New Flow">
                            <span className="glyphicon glyphicon-plus" />
                            <span>New Flow</span>
                        </button>

                        <button className="btn btn-sm btn-default" onClick={this.onClickRefresh}>
                            <span className="glyphicon glyphicon-refresh" />
                        </button>
                    </div>
                </div>
                { content }
            </div>
        );
    }
}

const mapStateToProps = state => ({
    authenticationToken: state.currentUser.authenticationToken,
    flows: state.flows.all,
    isFlowDeleting: state.flows.isDeleting,
    isLoading: state.flows.isLoading,
    tenantId: state.currentUser.tenantId,
});

const mapDispatchToProps = ({
    deleteFlow,
    fetchAllFlows,
});

export default connect(mapStateToProps, mapDispatchToProps)(FlowList);
